import { GqlOrganization } from 'core/gql/fragments/organization';
import Division from 'models/division';
import Team from 'models/team';
import { VisibilityRule } from './AdditionalQuestionConditionalLogic/ConditionalLogic';

export type FormResult = {
  formResultId: string;
  registrantEmail: string;
  participantFirstName: string;
  participantLastName: string;
  rosterStatus: string;
  seasonParticipantId: string;
  rootDivisionId: string;
  status: string;
  orderId: number | null;
  orderNumber: string;
  orderBalance: number | string;
  participantParticipationGroup: string;
  waitlistStatus?: string;
};

export interface RegistrationFormProps {
  orgId: number;
  isModalOpen: boolean;
  setIsModalOpen: (boolean) => void;
  prevPath: string;
  org: GqlOrganization;
}

export interface RegistrationSeason {
  id: number;
  name: string;
  isArchived: boolean;
  tsDivisionId: number;
  divisions: Division[];
  endDate: string;
  teams: Team[];
}

export interface RegistrationProgram {
  id: number;
  name: string;
  seasons: RegistrationSeason[];
}

export type GroupBirthdateRow = {
  toggled: boolean;
  start: string;
  end: string;
};

export type GroupGenderRow = {
  toggled: boolean;
  options: string[];
};

export type GroupAutoRosterOption = {
  value: string;
  label: string;
};

export type GroupAutoRosterOptionGroup = {
  options: GroupAutoRosterOption[];
  label: string;
};

export type GroupAutoRosteringRow = {
  toggled: boolean;
  options: GroupAutoRosterOptionGroup[];
  rosterScopeId?: number; // Where will the participants be rostered
  rosterScopeType?: string; // What type of destination will the participants be rostered to (team or division)
  rosterMode?: string; // How will the participants be rostered (builder or instant)
  error?: string | null;
};

export interface GroupEligibilityRow {
  active: boolean;
  name: string;
  id: number;
  expanded: boolean;
  birthdates: GroupBirthdateRow;
  genders: GroupGenderRow;
  autoRostering: GroupAutoRosteringRow;
}

export interface GroupCapacityRow {
  active: boolean;
  name: string;
  id: number;
  expanded: boolean;
  toggled: boolean;
  maxCapacity: number;
  error?: string;
  waitlistAllowed: boolean;
}

export interface RegistrationType {
  name: {
    value: string;
    touched: boolean;
  };
  program: {
    id: number;
    value: string;
    touched: boolean;
  };
  season: {
    id: number;
    value: string;
    touched: boolean;
  };
  paymentAccount: {
    value: string;
    touched: boolean;
  };
}

export interface FeeType {
  id: number;
  amount: string;
}

export interface FieldDefinitionType {
  id: number;
  name: string;
  target: string;
  type: string;
  label: string;
  group: string;
  required_default: boolean;
  locked: boolean;
  prefill: boolean;
  active: boolean;
  order: number;
  organization_id: number;
  options: string[];
  multiselect: boolean;
  enabled: boolean;
  internal: boolean;
  external_id: number | null;
  external_type: string | null;
}

export interface FieldType {
  id: number;
  column: number;
  row: number;
  target: string;
  required: boolean;
  form_field_definition_id: number;
  label: string;
  name: string;
  locked: boolean;
  group: string;
  type: string;
  organization_id: number | null;
  order: number;
  options: string[];
  multiselect: boolean;
  enabled: boolean;
  question_number: number;
  visibility_rules?: VisibilityRule[];
  internal: boolean;
  helpText: string;
}

export type QuestionsType = {
  guardian: FieldType[];
  participant: FieldType[];
  additional: FieldType[];
};

export type SignatureType = 'checkbox_accept' | 'initials' | 'full_name';

export interface Waiver {
  id: number;
  title: string;
  content: string;
  uuid: string;
  signatureType: SignatureType;
  active: boolean;
  attachment?: File;
  filename: string;
  hasFormWaiverResults?: boolean;
  originalFileName?: string;
  externalId?: string | null;
}

export interface FormMessageType {
  id: number;
  content: string;
  touched: boolean;
}

export interface FormFieldsType {
  touched: boolean;
  value: FieldType[];
}

export interface FormWaiversType {
  touched: boolean;
  value: Waiver[];
}

export interface FormFeesType {
  touched: boolean;
  value: FeeType[];
}

export interface GroupEligibilityType {
  touched: boolean;
  value: GroupEligibilityRow[];
}

export interface GroupCapacityType {
  touched: boolean;
  value: GroupCapacityRow[];
}

export interface State {
  registration: RegistrationType;
  dates: string;
  fees: FormFeesType;
  participantGroups: ParticipantGroupsType;
  formFields: FormFieldsType;
  waivers: FormWaiversType;
  message: FormMessageType;
  groupEligibility: GroupEligibilityType;
  groupCapacity: GroupCapacityType;
}

export interface ParticipantGroupsType {
  touched: boolean;
  value: ParticipantGroup[];
}

export interface ParticipantGroup {
  id?: number;
  active?: boolean;
  name: string;
  description: string;
  participantFee?: number;
  from_birthdate_range?: string;
  to_birthdate_range?: string;
  gender_options?: string;
  capacity_limit?: number;
  waitlist_allowed?: boolean;
  expanded?: boolean;
  hasFormResults?: boolean;
  roster_scope_type?: string;
  roster_scope_id?: number;
  roster_mode?: 'builder' | 'instant';
  participant_fee?: number;
}

export interface ParticipantGroupUpdate {
  id?: number;
  name?: string;
  description?: string;
  participantFee?: number;
}

export interface PaymentAccountInfo {
  paymentAccountId: number | null;
  organizationPaysProcessingFee: boolean;
  allowOfflineCheckout: boolean;
}

export interface InstallmentPlan {
  id: number;
  name?: string;
  enabled?: boolean;
  depositType?: string;
  depositAmount?: number;
  installmentCount?: number;
  frequencyType?: string; // monthly, weekly, custom
  frequencyAmount?: number;
  withdrawDay?: string;
  withdrawBasedOnCheckout?: boolean;
  scopeId?: number;
  scopeType?: string;
  applyDepositPerRegistration?: boolean;
  scheduledDates?: string[];
}

export interface LineItem {
  name: string;
  amount: number;
  canceled?: boolean;
  externalId?: number;
  externalType?: string;
  id?: number;
  insertedAt?: string;
  orderId?: number;
  scopeId?: number;
  scopeType?: string;
  sourceId?: number;
  sourceType?: string;
  type?: string;
  updatedAt?: string;
}
export interface InstallmentPaymentMethod {
  accountName: string;
  last4: string;
  type: string;
}
export interface Installment {
  id: number;
  amount: number;
  paymentMethodId?: number;
  scheduledDate?: string;
  status: string;
  paymentMethod?: InstallmentPaymentMethod;
}

export interface InstallmentEditable {
  _key: string;
  id?: number;
  amount?: number;
  scheduledDate?: string;
  originalDate?: string;
  status?: string;
  canceled?: boolean;
}
export const MAX_TOTAL_INSTALLMENTS = 24;
export const EDITABLE_INSTALLMENT_STATUSES = [
  'unpaid',
  'scheduled',
  'failed',
  'pending_cash_payment',
];

// Registration Navigation
// TODO IMPORTANT: Detach this enum from stepper behaviour
export enum RegistrationSteps {
  PROGRAMS,
  SEASON,
  NAME,
  SCHEDULE,
  NGB_INTEGRATION,
  GROUP_NAMES,
  REGISTRATION_FEES,
  ELIGIBILITY_RULES,
  CAPACITY_LIMIT,
  QUESTIONS_LOGIC,
  SIGNATURES,
  DEPOSIT_ACCOUNT,
  INSTALLMENTS,
  DISCOUNTS,
  ADDONS,
  CONFIRMATION_MESSAGE,
}

export const GENERAL_STEPS = [
  RegistrationSteps.PROGRAMS,
  RegistrationSteps.SEASON,
  RegistrationSteps.NAME,
  RegistrationSteps.SCHEDULE,
];

export const VALID_GENERAL_STEPS = [
  'registration.program.value',
  'registration.season.value',
  'registration.name.value',
  {
    key: 'dates',
    not: ['null - null', 'None Added'],
  },
];

export const PARTICIPANT_GROUP_STEPS = [
  RegistrationSteps.GROUP_NAMES,
  RegistrationSteps.REGISTRATION_FEES,
  RegistrationSteps.ELIGIBILITY_RULES,
  RegistrationSteps.CAPACITY_LIMIT,
];

export const VALID_PARTICIPANT_GROUP_STEPS = ['participantGroups.value'];

export const FORM_BUILDER_STEPS = [
  RegistrationSteps.QUESTIONS_LOGIC,
  RegistrationSteps.SIGNATURES,
];

export const VALID_FORM_BUILDER_STEPS = ['formFields.value', 'waivers.value'];

export const CHECKOUT_SETTINGS_STEPS = [
  RegistrationSteps.DEPOSIT_ACCOUNT,
  RegistrationSteps.INSTALLMENTS,
  RegistrationSteps.DISCOUNTS,
];

export const VALID_CHECKOUT_SETTINGS_STEPS = [
  'registration.paymentAccount.value',
];

export const MESSAGING_STEPS = [RegistrationSteps.CONFIRMATION_MESSAGE];

export const ALL_VALID_DATA = [
  ...VALID_GENERAL_STEPS,
  ...VALID_PARTICIPANT_GROUP_STEPS,
  ...VALID_FORM_BUILDER_STEPS,
];

export const VALID_MESSAGING_STEPS = [
  {
    key: 'message.id',
    not: null,
  },
];

export type PartnerOffering = {
  id: number;
  name: string;
  productName: string;
  description: string;
  requiresFees: boolean;
};

export type RegistrationAddonState = {
  partnerOfferingId: number;
  enabled: boolean;
};
